import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import strings from '../Globalization';
import LoadingDialog from '../components/common/LoadingDialog';
import consts from '../consts';
import { useSetState, useUniqueEffect } from '../hooks';
import connectionService from '../services/Connection';
import util from '../util';
import XmlEditorDialog from '../contexts/CycleInfo/XmlEditorDialog';
import { useCycleInfo } from '../contexts/CycleInfo/Context';
import TrendsLineChart from '../contexts/CycleInfo/TrendsLineChart';
import AdvantageCycleInfoProvider from './advantagecycleinfoviews/Context';
import CriticalParametersTable from './advantagecycleinfoviews/CriticalParametersTable';
import CycleBreadcrumbs from './advantagecycleinfoviews/CycleBreadcrumbs';
import CycleReviewFormDialog from './advantagecycleinfoviews/CycleReviewFormDialog';
import TopSection from './advantagecycleinfoviews/TopSection';
import { getPrintDetailsParams } from './advantagecycleinfoviews/dataUtil';
import { CIResultFormDialog, PhaseTabs, Receipt } from './cycleinfoviews';
import ProcessMonitor from './advantagecycleinfoviews/ProcessMonitor'

const DTE_FIELDS = [
	'ActualConditionTime',
	'ActualSterilizeTime',
	'CreatedOn',
	'CycleDateTime',
	'CycleStart',
	'CycleEnd',
	'DisinfectionContactTime',
	'DryTime',
	'FinalRinseContactTime',
	'ModifiedOn',
	'ReviewTimeStamp',
	'SterilizeTimeActual',
	'SterilizeTimeMetric',
	'VHPExpirationActual',

];

const AdvantageCycleInfo = () => {
	const theme = useTheme();
	const params = useParams();
	const navigate = useNavigate();
	const tapeRef = useRef();
	const comboTypes = [{ "type": "CycleActionStatus", "loaded": true }]
	const { stockChartRef } = useCycleInfo();
	const [searchParams, setSearchParams] = useSearchParams();
	const clientId = 0;
	
	const [state, setState] = useSetState({
		data: null,
		combos: {},
		loading: false,
		loadingMessage: null,
		isLog: true,
		error: null,
		topSection: [],
		modalIsXmlOpen: false,
		xmlEditorValue: '',
		view: 'details',
		isChartPopUp: false,
		filterOptions: {},
		isShowInstrument: false,
		filters: {},
		disablePrev: false,
		disableNext: false,
		printButtonClicked: false,
		isXmlProcess: false,
		ciResultFormDialogOpen: false,
		cycleReviewFormDialogOpen: false,
		xmlEditorOpen: false
	});
	const id = useMemo(() => searchParams.has('id') ? searchParams.get('id') : state.data?._id, [searchParams, state.data]);
	const cycleId = useMemo(
		() =>
			searchParams.has('cycleId') ? searchParams.get('cycleId') : state.data?.CycleId,
		[searchParams, state.data]
	);
	
	const modelName = state.data && state.data.ModelName;
	const isChemistry = state.data && !!state.data?.ChemistryType;

	const [isPerformanceAccess] = useMemo(
		() => [util.checkAccess('PerformanceMonitorAccess'), util.getDateTimeFormat()],
		[]
	);

	const getFormData = useCallback(
		(action) => {
			let formData = new FormData();
			formData.append('authToken', null);
			formData.append('action', action);
			formData.append('attachmentCount', '0');
			formData.append('id', id);
			formData.append('comboTypes', JSON.stringify(comboTypes));

			return formData;
		},
		[params, comboTypes, id]
	);

	const fetchData = async (params) => {
		setState({
			loading: true
		});
		fetch(connectionService.AdvantageCycleList, {
			method: 'POST',
			...params
		})
			.then((res) => res.json())
			.then(({ data, combos }) => {
				DTE_FIELDS.forEach((field) => {
					data[field] = util.fixDates(data[field], { utc: true });
				});
				setState({ loading: false, data, combos, topSection: data.TopSection });
			})
			.catch((err) => {
				console.error(err)
			})
			.finally(() =>
				setState({
					loading: false
				})
			);
	};

	const onOpenXmlModal = useCallback(() => {
		setState({ loading: true });
		const xmlEditorTitle = `${strings.XMLName} ${state.data?.EquipmentTypeName}/${state.data?.SerialNumber}`;
		const formData = new FormData();
		formData.append('action', 'otherAction');
		formData.append('otherAction', 'PreviewXML');
		formData.append('cycleId', state.data?.CycleId);
		fetch(connectionService.CycleList, {
			method: 'POST',
			body: formData
		})
			.then((res) => res.text())
			.then((xmlEditorValue) => {
				setState({ xmlEditorOpen: true, xmlEditorValue, loading: false, xmlEditorTitle });
			})
			.catch((error) => setState({ error }))
			.finally(() => setState({ loading: false }));
	}, [state.data]);


	const handleOnLoadNextOrPrev = useCallback(
		({ id, equipmentId, type, isIncrement }) => {
			const formData = new FormData();
			setState({
				loading: true
			});
			const SerialNumber = state.data?.SerialNumber;
			equipmentId = equipmentId?._id || equipmentId;
			formData.append('action', 'otherAction');
			formData.append('otherAction', 'NextPrevHandler');
			formData.append('equipmentId', equipmentId);
			formData.append('id', id);
			formData.append('asArray', false);
			formData.append('type', type);
			formData.append('isIncrement', isIncrement);
			fetch(connectionService.CycleList, {
				method: 'POST',
				body: formData
			})
				.then((res) => res.json())
				.then((json) => {
					if (json.data?.length) {
						const data = json.data[0];
						const newId = data?._id;
						if (newId) {
							searchParams.set('id', newId);
							searchParams.set('attachmentCount', data.AttachmentCount || 0);
							setSearchParams(searchParams);
						}
					} else {
						let message = isIncrement
							? strings.formatString(strings.NextPage, SerialNumber)
							: strings.formatString(strings.PrevPage, SerialNumber);
						Swal.fire({
							titleText: strings.Info,
							text: message,
							icon: 'info',
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						});
					}
				})
				.catch((err) => {
					Swal.fire({
						titleText: strings.Error,
						text: strings[`Status_${err?.status}`],
						icon: 'error',
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
				})
				.finally(() =>
					setState({
						loading: false
					})
				);
		},
		[cycleId, state.data, searchParams]
	);


	const reprocessXml = useCallback(() => {
		setState({ isXmlProcess: true });
		let formData = new FormData();
		formData.append('action', 'otherAction');
		formData.append('otherAction', 'ReprocessXml');
		formData.append('cycleId', cycleId);
		fetch(connectionService.CycleList, { method: 'POST', body: formData })
			.then((res) => res.json())
			.then((json) => {
				if (json.success) {
					Swal.fire({ titleText: strings.Success, text: strings.XmlReprocessed, icon: 'success', confirmButtonText: strings.Ok, allowOutsideClick: false });
					const params = getFormData('load');
					fetchData({ body: params });
				} else {
					Swal.fire({ titleText: strings.Success, text: strings.XmlProcessingFailed, icon: 'success', confirmButtonText: strings.Ok, allowOutsideClick: false });
				}
			})
			.catch((err) => Swal.fire({ titleText: strings.Error, text: strings.XmlProcessingFailed, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false }))
			.finally(() => setState({ isXmlProcess: false }));

	}, [cycleId]);

	const onNext = useCallback(() => {
		handleOnLoadNextOrPrev({
			id: state.data?._id,
			equipmentId: state.data?.EquipmentId,
			type: 'advantageCycle',
			isIncrement: true
		});
	}, [handleOnLoadNextOrPrev, state.data?.EquipmentId, state.data?._id]);


	const onPrev = useCallback(() => {
		handleOnLoadNextOrPrev({
			id: state.data?._id,
			equipmentId: state.data?.EquipmentId,
			type: 'advantageCycle',
			isIncrement: false
		});
	}, [handleOnLoadNextOrPrev, state.data?.EquipmentId, state.data?._id]);

	const onPrintTape = useCallback(() => {
		if (tapeRef.current?.download) {
			const { SerialNumber, CycleName, CycleCount, CycleStart } = state.data;
			const filename = `${SerialNumber}_${CycleName.replace(/ /g, '')}_${CycleCount}_${dayjs(CycleStart).format(
				consts.MomentFormat.MonthDateYear
			)}_Print_Tape`;
			setState({ loading: true })
			tapeRef.current.download(filename, 'pdf').finally(() => setState({ loading: false }));
		}
	}, [state.data]);


	const onPreviewTape = () => {
		tapeRef.current?.print?.();
	};

	const onCloseCIResultFormDialog = () => setState({ ciResultFormDialogOpen: false });
	const onOpenCycleReviewFormDialog = (approvalStatusId) =>
		setState({ cycleReviewFormDialogOpen: true, approvalStatusId });
	const onCloseCycleReviewFormDialog = (reload = false) => {
		setState({ cycleReviewFormDialogOpen: false })
		if (reload) {
			const formData = getFormData('load');
			fetchData({ body: formData });
		}
	};



	const onPreviewDetails = useCallback((preview = true) => {
		setState({ loading: true });
		const params = getPrintDetailsParams({ data: state.data, isChemistry });
		const formData = new FormData();
		for (let { Name, Value } of params) {
			formData.append(Name, Value);
		}
		fetch(connectionService.CycleList, { method: 'POST', body: formData })
			.then((res) => res.blob())
			.then((blob) => {
				if (blob) {
					const { SerialNumber, CycleName, CycleCount, CycleStart } = state.data;
					const filename = `${SerialNumber}_${CycleName.replace(/ /g, '')}_${CycleCount}_${dayjs(CycleStart).format(consts.MomentFormat.MonthDateYear)}`;
					const href = window.URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = href;
					document.body.appendChild(link);
					if (preview) {
						link.setAttribute('target', `_blank`);
						link.setAttribute('type', `application/pdf`);
					} else {
						link.setAttribute('download', `${filename}.pdf`);
					}
					link.click();
					document.body.removeChild(link);
				}
			})
			.finally(() => setState({ loading: false }));
	}, [state.topSection, isChemistry, state.data]);

	const onPhasesFilter = (filters) => {
		setState({
			filters
		});
	};

	const onPrintDetails = useCallback(() => onPreviewDetails(false), [state.data]);

	const onCloseXmlEditorDialog = () =>
		setState({ xmlEditorOpen: false, xmlEditorValue: undefined, xmlEditorTitle: undefined });

	useUniqueEffect(() => {
		const newSearchParams = new URLSearchParams(searchParams);
		if(newSearchParams.has('tab') && modelName){
			setState(prevParams => {
				return { ...prevParams, view: 'processMonitor' };
			});
		}
	}, []);

	useUniqueEffect(() => {
		if (id) {
			const formData = getFormData('load');
			fetchData({ body: formData });
		} else {
			navigate(`/cyclelist`);
		}
	}, [id, state.view]);

	useUniqueEffect(() => {
		if (!isPerformanceAccess) {
			Swal.fire({
				titleText: strings.Alert,
				text: strings.AccessError,
				showCancelButton: false,
				confirmButtonText: strings.Ok,
				confirmButtonClass: 'btn-danger',
				closeOnConfirm: true,
				allowOutsideClick: false
			}).then(() => {
				navigate(`/`);
			});
		}
	}, [isPerformanceAccess]);

	const onChangeView = useCallback(() => {
		setState(prevParams => {
			const newView = prevParams.view === 'details' ? 'processMonitor' : 'details';
			const newSearchParams = new URLSearchParams(searchParams);
			if (newSearchParams.has('tab')) {
				newSearchParams.delete('tab');
				setSearchParams(newSearchParams);
			}
			return { ...prevParams, view: newView };
		});
	}, []);

	const breadcrumbsProps = useMemo(
		() => ({
			startActions: consts.regex.IsaOrRepidAer.test(modelName)
				? [
					{
						color: 'primary',
						disableElevation: true,
						text: state.view === 'processMonitor' ? strings.CycleLogs : strings.ProcessMonitor || 'Process Monitor',
						variant: 'contained',
						size: 'small',
						onClick: onChangeView
					}
				]
				: []
		}),
		[setState, state.view, onChangeView, modelName]
	);

	return (
		<AdvantageCycleInfoProvider
			value={{
				...state,
				clientId,
				cycleId,
				isChemistry,
				isPerformanceAccess,
				reprocessXml,
				onPrintTape,
				onCloseCycleReviewFormDialog,
				onCloseCIResultFormDialog,
				onPreviewDetails,
				onPrintDetails,
				onPhasesFilter,
				disablePrev: state.disablePrev,
				disableNext: state.disableNext,
				onOpenXmlModal,
				onCloseXmlEditorDialog,
				onPreviewTape,
				onOpenCycleReviewFormDialog,
				onNext,
				onPrev,
				xmlFileName: state.data?.StorageFileName,
				onChangeView
			}}
		>
			<Grid container sx={{ paddingTop: theme.spacing(2) }}>
				<Grid size={12}>
					<LoadingDialog isOpen={state.loading} message={state.loadingMessage} />
				</Grid>

				{!!state.data && (
					<Grid size={12} container item>
						<CycleBreadcrumbs {...breadcrumbsProps} />
						<CIResultFormDialog
							data={state.data}
							open={state.ciResultFormDialogOpen}
							onDismiss={onCloseCIResultFormDialog}
						/>
						<CycleReviewFormDialog />
						<XmlEditorDialog
							xmlEditorOpen={state.xmlEditorOpen}
							xmlEditorValue={state.xmlEditorValue}
							xmlEditorTitle={state.xmlEditorTitle}
							xmlFileName={state.data?.StorageFileName}
							onCloseXmlEditorDialog={onCloseXmlEditorDialog}
						/>
					</Grid>
				)}
				<Grid size={12} container>
					{!!state.data &&  state.view === 'details' &&(
						<Card sx={{
							width: '100%',
							'& .MuiCardContent-root:last-child': {
								paddingBottom: '0px'
							}
							}}>
							<CardContent>
								<Grid container>
									{state.view === 'details' ? (
										!!state.data &&
										Object.keys(state.data)?.length > 0 && (
											<Grid container>
												<Grid size={12}>
													<TopSection />
												</Grid>
											</Grid>
										)
									) : (
										<Grid container>
											<Grid size={12}>
												{!!state.data?.ChartDetail && (
													<Grid container size={12} className="wrapper wrapper-content">
														<Grid size={12}>
															<PhaseTabs data={state.data} />
														</Grid>
													</Grid>
												)}
											</Grid>
										</Grid>
									)}
								</Grid>
							</CardContent>
						</Card>
					)}
				</Grid>
			</Grid>
			{state.view === 'processMonitor' && (
				<ProcessMonitor type="ADV" />

			)}
			{state.data?.CycleName !== 'Chemistry Change' && state.view !== 'processMonitor' && (
				<Grid container sx={{ paddingTop: theme.spacing(2) }}>
					<Grid size={12} container>
						{!!state.data && (
							<Card sx={{ width: '100%' }}>
								<CardContent>
									<Grid container size={12}>
										{state.view === 'details' && (
											Object.keys(state.data)?.length > 0 && (
												<Grid container size={12}>
													<Grid
														container
														className="wrapper page-heading"
														sx={{ paddingTop: theme.spacing(3) }}
														size={12}
													>
														<Grid container size={12}>
															<Grid item size={12}>
																<Box
																	display="flex"
																	rowGap={1}
																	columnGap={1}
																	flexDirection={{ xs: 'column', lg: 'row' }}
																>
																	<Box flexGrow={1}>
																		{state.data?.CycleName !== 'Chemistry Change' && (
																			<CriticalParametersTable />
																		)}
																		{state.data?.ChartDetail?.isPhasesDataAvailable && (
																			<>
																				<Typography
																					marginTop={5}
																					variant="subtitle1"
																					fontWeight={'bold'}
																				>
																					{strings.PRESSURELOSSLEAKANDFLOWCHECKTEMPERATURE}
																				</Typography>
																				<TrendsLineChart cycleInfo={'useAdvantageCycleInfo'} ref={stockChartRef} />
																			</>
																		)}
																	</Box>
																	{state.data?.CycleTape?.length > 0 && (
																		<Box id="tape-container">
																			<Receipt content={state.data?.CycleTape} languageCode={state.data.CycleLanguageCode} ref={tapeRef} />
																		</Box>
																	)}
																</Box>
															</Grid>
														</Grid>
													</Grid>
												</Grid>
											)
										)}
									</Grid>
								</CardContent>
							</Card>
						)}
					</Grid>
				</Grid>
			)}
		</AdvantageCycleInfoProvider>
	);
};
export default AdvantageCycleInfo;